import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import themestyle from "../theme";
import PhoneNumberField from "../components/PhoneNumberField";
import AppDatePicker from "./AppDatePicker";
import { getPhoneValidationMessage } from "../utils/phoneValidation";
import {
  textFieldSX,
  FormLabel,
  autocompleteListboxSX,
} from "../utils/formField";
import { ASSIGN_TYPE_OPTIONS, CLASS_BUTTONS, MSG, MESSAGES, REGEX, FORM_PLACEHOLDERS } from "../utils/const";
import CustomSnackbar from "../components/Snackbar";
import StudentAuditHistory from "../components/StudentAuditHistory";
import { useMutation, useQuery } from "@apollo/client/react";
import { CREATE_STUDENT, UPDATE_STUDENT } from "../graphql/mutation";
import { GET_STUDENT_BY_ID, GET_CLASS_NAMES, GET_STUDENT_LIST, GET_DASHBOARD_COUNTS } from "../graphql/query";
import type {
  CreateStudentInput,
  CreateStudentMutationResponse,
  UpdateStudentMutationResponse,
  GetStudentByIdResponse,
  StudentFormMode,
} from "../interfaces/student.interface";

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const NAME_REGEX = /^[A-Za-z\s]*$/;

type AddStudentFormProps = {
  mode?: StudentFormMode;
  id?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
  onDelete?: (id: string, name: string, assignedClasses?: string[]) => void;
};

const AddStudentForm: React.FC<AddStudentFormProps> = ({
  mode = "add",
  id,
  onSuccess,
  onCancel,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    fullName: "",
    studentId: "",
    email: "",
    password: "",
    phoneNumber: "",
    additionalPhoneNumber: "",
    dateOfBirth: "",
    address: "",
    assignType: "",
    assignToClass: [] as string[],
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">(
    "success",
  );
  const [showPassword, setShowPassword] = useState(false);

  const isViewMode = mode === "view";

  const { data: studentData } = useQuery<
    GetStudentByIdResponse,
    { id: string }
  >(GET_STUDENT_BY_ID, {
    variables: { id: id || "" },
    skip: mode === "add" || !id,
    fetchPolicy: "network-only",
  });

  const { data: classData } = useQuery(GET_CLASS_NAMES, {
    variables: { page: 1, perPage: 100 },
    fetchPolicy: "network-only",
  });

  const classOptions = useMemo(() => {
    if (!classData?.getClassList?.data) return [];
    const uniqueOptions = new Map<string, { label: string; value: string; className: string; instructorName: string }>();
    classData.getClassList.data.forEach((c: any) => {
      const optionLabel = `${c.className} - ${c.instructorName}`;
      if (!uniqueOptions.has(optionLabel)) {
        uniqueOptions.set(optionLabel, {
          label: optionLabel,
          value: optionLabel,
          className: c.className,
          instructorName: c.instructorName
        });
      }
    });

    return Array.from(uniqueOptions.values());
  }, [classData]);

  const [createStudent, { loading: createLoading }] = useMutation<
    CreateStudentMutationResponse,
    { input: CreateStudentInput }
  >(CREATE_STUDENT, {
    refetchQueries: [
      { query: GET_STUDENT_LIST, variables: { page: 1, perPage: 4 } },
      { query: GET_STUDENT_LIST, variables: { page: 1, perPage: 10 } },
      { query: GET_DASHBOARD_COUNTS },
    ],
    awaitRefetchQueries: true,
  });

  const [updateStudent, { loading: updateLoading }] = useMutation<
    UpdateStudentMutationResponse,
    { id: string; input: any }
  >(UPDATE_STUDENT, {
    refetchQueries: [
      { query: GET_STUDENT_LIST, variables: { page: 1, perPage: 4 } },
      { query: GET_STUDENT_LIST, variables: { page: 1, perPage: 10 } },
      { query: GET_DASHBOARD_COUNTS },
    ],
    awaitRefetchQueries: true,
  });

  const loading = createLoading || updateLoading;

  useEffect(() => {
    if (
      studentData?.getStudentById?.success &&
      studentData.getStudentById.data
    ) {
      const data = studentData.getStudentById.data;
      setFormData({
        fullName: data.fullName || "",
        studentId: data.studentId || "",
        email: data.email || "",
        password: "",
        phoneNumber: data.phoneNumber || "",
        additionalPhoneNumber: data.additionalPhoneNumber || "",
        dateOfBirth: data.dateOfBirth || "",
        address: data.address || "",
        assignType: data.assignType || "",
        assignToClass: data.assignToClass || [],
        isActive: data.isActive ?? true,
      });
    }
  }, [studentData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // The country-code phone picker reports a plain string (not a DOM event), so it gets
  // its own change/blur handlers that still run the same field validation.
  const handlePhoneFieldChange =
    (name: "phoneNumber" | "additionalPhoneNumber") => (next: string) => {
      setFormData((prev) => ({ ...prev, [name]: next }));
      setErrors((prev) => ({ ...prev, [name]: validateField(name, next) }));
    };
  const handlePhoneFieldBlur =
    (name: "phoneNumber" | "additionalPhoneNumber") => () => {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };

  const validateField = (name: string, value: any) => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (name === "fullName") {
      if (!trimmed) return "Full name is required";
      if (trimmed.length < 2) return "Name must be at least 2 characters";
      if (!NAME_REGEX.test(trimmed))
        return "Name cannot contain numbers or special characters";
      return "";
    }
    if (name === "email") {
      if (!trimmed) return "Email is required";
      if (!EMAIL_REGEX.test(trimmed)) return "Invalid email address";
      return "";
    }
    if (name === "password" && mode === "add") {
      if (!trimmed) return MESSAGES.PASS_REQUIRED;
      if (trimmed.length < 8) return MESSAGES.PASSWORD_MIN;
      if (!REGEX.UPPERCASE.test(trimmed)) return MESSAGES.PASSWORD_UPPER;
      if (!REGEX.LOWERCASE.test(trimmed)) return MESSAGES.PASSWORD_LOWER;
      if (!REGEX.NUMBER.test(trimmed)) return MESSAGES.PASSWORD_NUMBER;
      if (!REGEX.SPECIAL_CHAR.test(trimmed)) return MESSAGES.PASSWORD_SPECIAL;
      return "";
    }
    if (name === "phoneNumber") {
      if (!trimmed) return "Phone number is required";
      // PhoneNumberField stores "+<dialCode> <national>"; libphonenumber validates accurately.
      return getPhoneValidationMessage(trimmed);
    }
    if (name === "additionalPhoneNumber") {
      if (!trimmed) return "";
      return getPhoneValidationMessage(trimmed);
    }
    if (name === "dateOfBirth" && !trimmed) return "Date of birth is required";
    if (name === "address") {
      if (!trimmed) return MSG.ADDRESS_REQUIRED;
      if (trimmed.length < 5) return MSG.ADDRESS_MIN;
      return "";
    }

    return "";
  };

  const handleSubmit = async () => {
    const fieldErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "additionalPhoneNumber" || key === "assignToClass" || key === "isActive" || key === "assignType") return;
      if (key === "password" && mode !== "add") return;
      const message = validateField(key, value);
      if (message) fieldErrors[key] = message;
    });

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    const { studentId, password, email, ...rest } = formData;
    const input: any =
      mode === "add" ? { ...rest, email, password } : { ...rest, studentId };
    if (mode === "add") {
      input.password = password;
      input.email = email;
    } else {
      delete input.password;
      delete input.email;
    }

    try {
      if (mode === "edit" && id) {
        const { data } = await updateStudent({ variables: { id, input } });
        if (!data?.updateStudent?.success) {
          setSnackbarMessage(
            data?.updateStudent?.message || "Failed to update student",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(data.updateStudent.message || "Student updated");
        setSnackbarType("success");
      } else {
        const { data } = await createStudent({ variables: { input } });
        if (!data?.createStudent?.success) {
          setSnackbarMessage(
            data?.createStudent?.message || "Failed to create student",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(data.createStudent.message || "Student created");
        setSnackbarType("success");
      }
      setSnackbarOpen(true);
      if (onSuccess) {
        setTimeout(onSuccess, 900);
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  const isFormValid = () => {
    const requiredFieldsFilled =
      formData.fullName.trim() !== "" &&
      formData.email.trim() !== "" &&
      (mode !== "add" || formData.password.trim() !== "") &&
      formData.phoneNumber.trim() !== "" &&
      formData.dateOfBirth.trim() !== "" &&
      formData.address.trim() !== "";

    const noErrors = Object.values(errors).every((error) => !error);

    return requiredFieldsFilled && noErrors;
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Student Information
          </Typography>
        </Box>

        <Box
          p={3}
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ px: { xs: 2, sm: 3, md: 5 } }}
        >
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Full Name{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                name="fullName"
                value={formData.fullName}
                placeholder="Enter full name"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.fullName}
                helperText={errors.fullName}
                disabled={isViewMode}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Email Address{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                name="email"
                value={formData.email}
                placeholder="example@mail.com"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.email}
                helperText={errors.email}
                disabled={isViewMode || mode === "edit"}
              />
            </Box>
            <Box flex={1}>
              {mode === "add" ? (
                <>
                  <FormLabel>
                    Password{" "}
                    <span style={{ color: themestyle.colors.error }}>*</span>
                  </FormLabel>
                  <TextField
                    fullWidth
                    sx={textFieldSX}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    placeholder="Enter password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.password}
                    helperText={errors.password}
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? (
                              <VisibilityIcon fontSize="small" />
                            ) : (
                              <VisibilityOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              ) : (
                <>
                  <FormLabel>
                    Phone Number{" "}
                    <span style={{ color: themestyle.colors.error }}>*</span>
                  </FormLabel>
                  <PhoneNumberField
                    value={formData.phoneNumber}
                    placeholder={FORM_PLACEHOLDERS.PHONE_UK}
                    disabled={isViewMode}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    onChange={handlePhoneFieldChange("phoneNumber")}
                    onBlur={handlePhoneFieldBlur("phoneNumber")}
                  />
                </>
              )}
            </Box>
          </Box>
          {mode === "add" && (
            <Box
              display="flex"
              gap={3}
              flexDirection={{ xs: "column", md: "row" }}
            >
              <Box flex={1}>
                <FormLabel>
                  Phone Number{" "}
                  <span style={{ color: themestyle.colors.error }}>*</span>
                </FormLabel>
                <PhoneNumberField
                  value={formData.phoneNumber}
                  placeholder={FORM_PLACEHOLDERS.PHONE_UK}
                  disabled={isViewMode}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  onChange={handlePhoneFieldChange("phoneNumber")}
                  onBlur={handlePhoneFieldBlur("phoneNumber")}
                />
              </Box>
              <Box flex={1}>
                <FormLabel>Additional Phone Number</FormLabel>
                <PhoneNumberField
                  value={formData.additionalPhoneNumber}
                  placeholder={FORM_PLACEHOLDERS.PHONE_UK}
                  disabled={isViewMode}
                  error={!!errors.additionalPhoneNumber}
                  helperText={errors.additionalPhoneNumber}
                  onChange={handlePhoneFieldChange("additionalPhoneNumber")}
                  onBlur={handlePhoneFieldBlur("additionalPhoneNumber")}
                />
              </Box>
            </Box>
          )}
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            {mode === "add" ? (
              <>
                <Box flex={1}>
                  <FormLabel>
                    Assign Pass{" "}
                  </FormLabel>
                  <Autocomplete
                    options={ASSIGN_TYPE_OPTIONS}
                    disabled={isViewMode}
                    value={
                      ASSIGN_TYPE_OPTIONS.find(
                        (opt: { label: string; value: string }) =>
                          opt.value === formData.assignType,
                      ) || null
                    }
                    onChange={(_, value) => {
                      const val = value?.value || "";
                      setFormData((prev) => ({ ...prev, assignType: val }));
                      const error = validateField("assignType", val);
                      setErrors((prev) => ({ ...prev, assignType: error }));
                    }}
                    slotProps={{
                      listbox: { sx: autocompleteListboxSX },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Pass"
                        sx={textFieldSX}
                        error={!!errors.assignType}
                        helperText={errors.assignType}
                        onBlur={() => {
                          const error = validateField(
                            "assignType",
                            formData.assignType,
                          );
                          setErrors((prev) => ({ ...prev, assignType: error }));
                        }}
                      />
                    )}
                  />
                </Box>
                <Box flex={1}>
                  <FormLabel>
                    Date of Birth{" "}
                    <span style={{ color: themestyle.colors.error }}>*</span>
                  </FormLabel>
                  <AppDatePicker
                    value={formData.dateOfBirth}
                    onChange={(v) => {
                      setFormData((prev) => ({ ...prev, dateOfBirth: v }));
                      setErrors((prev) => ({
                        ...prev,
                        dateOfBirth: validateField("dateOfBirth", v),
                      }));
                    }}
                    error={!!errors.dateOfBirth}
                    helperText={errors.dateOfBirth}
                    disabled={isViewMode}
                    maxDate={new Date().toISOString().split("T")[0]}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box flex={1}>
                  <FormLabel>Additional Phone Number</FormLabel>
                  <PhoneNumberField
                    value={formData.additionalPhoneNumber}
                    placeholder={FORM_PLACEHOLDERS.PHONE_UK}
                    disabled={isViewMode}
                    error={!!errors.additionalPhoneNumber}
                    helperText={errors.additionalPhoneNumber}
                    onChange={handlePhoneFieldChange("additionalPhoneNumber")}
                    onBlur={handlePhoneFieldBlur("additionalPhoneNumber")}
                  />
                </Box>
                <Box flex={1}>
                  <FormLabel>
                    Assign Pass{" "}
                  </FormLabel>
                  <Autocomplete
                    options={ASSIGN_TYPE_OPTIONS}
                    disabled={isViewMode}
                    value={
                      ASSIGN_TYPE_OPTIONS.find(
                        (opt: { label: string; value: string }) =>
                          opt.value === formData.assignType,
                      ) || null
                    }
                    onChange={(_, value) => {
                      const val = value?.value || "";
                      setFormData((prev) => ({ ...prev, assignType: val }));
                      const error = validateField("assignType", val);
                      setErrors((prev) => ({ ...prev, assignType: error }));
                    }}
                    slotProps={{
                      listbox: { sx: autocompleteListboxSX },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Pass"
                        sx={textFieldSX}
                        error={!!errors.assignType}
                        helperText={errors.assignType}
                        onBlur={() => {
                          const error = validateField(
                            "assignType",
                            formData.assignType,
                          );
                          setErrors((prev) => ({ ...prev, assignType: error }));
                        }}
                      />
                    )}
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Row 5: Classes & DOB (Edit/View mode only) */}
          {mode !== "add" && (
            <Box
              display="flex"
              gap={3}
              flexDirection={{ xs: "column", md: "row" }}
            >

              <Box flex={1}>
                <FormLabel>
                  Date of Birth{" "}
                  <span style={{ color: themestyle.colors.error }}>*</span>
                </FormLabel>
                <AppDatePicker
                  value={formData.dateOfBirth}
                  onChange={(v) => {
                    setFormData((prev) => ({ ...prev, dateOfBirth: v }));
                    setErrors((prev) => ({
                      ...prev,
                      dateOfBirth: validateField("dateOfBirth", v),
                    }));
                  }}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth}
                  disabled={isViewMode}
                  maxDate={new Date().toISOString().split("T")[0]}
                />
              </Box>
            </Box>
          )}
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Address{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <TextField
                fullWidth
                multiline
                rows={4}
                sx={textFieldSX}
                name="address"
                value={formData.address}
                placeholder="Street name,city,State"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.address}
                helperText={errors.address}
                disabled={isViewMode}
              />
            </Box>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={isMobile ? 1 : 2}
          p={isMobile ? 1.5 : 3}
          sx={{ pt: 0, mb: isMobile ? 3 : 5, pr: { xs: 2, md: 5 } }}
        >
          <Button
            variant="outlined"
            onClick={() => onCancel && onCancel()}
            sx={{
              borderColor: themestyle.colors.buttonGreen,
              color: themestyle.colors.black,
              textTransform: "none",
              fontWeight: 600,
              px: isMobile ? 1.5 : 2,
              borderRadius: "8px",
              height: isMobile ? "36px" : "44px",
              fontSize: isMobile ? "13px" : "16px",
              minWidth: isMobile ? "70px" : "100px",
            }}
          >
            {mode === "view" ? "Cancel" : CLASS_BUTTONS.CANCEL}
          </Button>
          {isViewMode && onEdit ? (
            <Button
              variant="contained"
              onClick={onEdit}
              sx={{
                backgroundColor: themestyle.colors.buttonGreen,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                px: isMobile ? 1.5 : 5,
                height: isMobile ? "36px" : "44px",
                fontSize: isMobile ? "13px" : "16px",
                minWidth: isMobile ? "90px" : "110px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: themestyle.colors.buttonGreenHover,
                },
              }}
            >
              {CLASS_BUTTONS.EDIT}
            </Button>
          ) : (
            !isViewMode && (
              <>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !isFormValid()}
                  sx={{
                    backgroundColor: themestyle.colors.buttonGreen,
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    px: isMobile ? 1.5 : 5,
                    height: isMobile ? "36px" : "44px",
                    fontSize: isMobile ? "13px" : "16px",
                    minWidth: isMobile ? "75px" : "110px",
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: themestyle.colors.buttonGreenHover,
                    },
                  }}
                >
                  {loading
                    ? "Saving"
                    : mode === "add"
                      ? CLASS_BUTTONS.ADD
                      : isMobile
                        ? "Update"
                        : CLASS_BUTTONS.UPDATE}
                </Button>
                {isMobile && mode === "edit" && id && onDelete && (
                  <Button
                    variant="contained"
                    onClick={() =>
                      onDelete(id, formData.fullName, formData.assignToClass)
                    }
                    sx={{
                      backgroundColor: themestyle.colors.error,
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      px: 1.5,
                      height: "36px",
                      fontSize: "13px",
                      minWidth: "70px",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: themestyle.colors.error,
                      },
                    }}
                  >
                    {CLASS_BUTTONS.DELETE}
                  </Button>
                )}
              </>
            )
          )}
        </Box>
      </Box>
      {isViewMode && id && <StudentAuditHistory studentId={id} />}
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};
export default AddStudentForm;
